import { BooleanInput } from '@angular/cdk/coercion';
import {
  booleanAttribute,
  Component,
  computed,
  forwardRef,
  input,
  linkedSignal,
  model,
  output,
  untracked,
  ViewChild,
} from '@angular/core';
import { type ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { provideIcons } from '@ng-icons/core';
import { lucideCalendar } from '@ng-icons/lucide';
import { type ChangeFn, type TouchFn } from '@spartan-ng/brain/forms';
import { BrnPopover, BrnPopoverContent, BrnPopoverTrigger } from '@spartan-ng/brain/popover';
import { hlm } from '@spartan-ng/helm/utils';
import { HlmIconComponent } from '@spartan-ng/ui-icon-helm';
import { HlmInputDirective } from '@spartan-ng/ui-input-helm';
import { HlmPopoverContentDirective } from '@spartan-ng/ui-popover-helm';
import type { ClassValue } from 'clsx';
import { HLM_EXPOSES_STATE_TOKEN } from './hlm-date-picker.component';
import { injectHlmDateRangePickerConfig } from './hlm-date-range-picker.token';
import { HlmCalendarRangeComponent } from '@spartan-ng/ui-calendar-helm';

export const HLM_DATE_RANGE_PICKER_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => HlmDateRangePickerComponent),
  multi: true,
};

@Component({
  selector: 'hlm-date-range-picker',
  standalone: true,
  imports: [
    HlmIconComponent,
    BrnPopover,
    BrnPopoverTrigger,
    BrnPopoverContent,
    HlmPopoverContentDirective,
    HlmCalendarRangeComponent,
    HlmInputDirective,
  ],
  providers: [
    HLM_DATE_RANGE_PICKER_VALUE_ACCESSOR,
    provideIcons({ lucideCalendar }),
    HLM_EXPOSES_STATE_TOKEN,
  ],
  template: `
    <brn-popover sideOffset="5" closeDelay="100">
      <div hlmInput [class.focus]="isOpen()" [class]="'flex overflow-hidden ' + userClass()">
        <button
          type="button"
          [class]="_computedClass()"
          [disabled]="_disabled()"
          brnPopoverTrigger
          [style.color]="iconColor()"
        >
          <hlm-icon
            hlm
            size="unset"
            name="lucideCalendar"
            displayStyle="flex"
            class="calendar-icon flex items-center justify-center text-inherit pl-3"
            [class.labelActive]="isOpen()"
            [color]="iconColor()"
          />

          @if (formattedDate(); as formattedDate) {
            <div [class.labelActive]="isOpen()">
              {{ formattedDate }}
            </div>
          } @else {
            <ng-content />
          }
        </button>
      </div>

      <div id="web-app" *brnPopoverContent="let ctx" style="background-color: transparent;">
        <div hlmPopoverContent [class]="'w-auto ' + userClass()" style="padding: 0 !important;">
          <hlm-calendar-range
            [calendarClass]="userClass()"
            [startDate]="_start()"
            [endDate]="_end()"
            [min]="min()"
            [max]="max()"
            [disabled]="_disabled()"
            (startDateChange)="_handleStartDateChange($event)"
            (endDateChange)="_handleEndDateChange($event)"
          />
        </div>
      </div>
    </brn-popover>
  `,
  host: {
    class: 'block',
  },
  styles: `
    .calendar-icon {
      width: 1em;
      height: 1em;
    }
  `,
})
export class HlmDateRangePickerComponent<T> implements ControlValueAccessor {
  @ViewChild(BrnPopover) popover!: BrnPopover;

  private readonly _config = injectHlmDateRangePickerConfig<T>();

  public readonly isOpen = input<boolean>(false);
  public readonly iconColor = input<string | undefined>(undefined);
  public readonly userClass = input<ClassValue>('');

  /** The minimum date that can be selected. */
  public readonly min = input<T>();

  /** The maximum date that can be selected. */
  public readonly max = input<T>();

  /** Determine if the date picker is disabled. */
  public readonly disabled = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  /** If true, the picker closes once a complete range has been selected. */
  public readonly autoCloseOnEndSelection = input<boolean, BooleanInput>(
    this._config.autoCloseOnEndSelection,
    {
      transform: booleanAttribute,
    },
  );

  /** The committed range. Only ever set once both ends are chosen. */
  public readonly date = model<[T, T] | undefined>();

  /** Defines how the dates should be displayed in the UI. */
  public readonly formatDates = input<(dates: [T | undefined, T | undefined]) => string>(
    this._config.formatDates,
  );

  /** Defines how the dates should be transformed before saving to model/form. */
  public readonly transformDates = input<(dates: [T, T]) => [T, T]>(this._config.transformDates);

  public readonly changed = output<[T, T] | null>();

  /**
   * In-flight selection. Reset whenever the committed range changes, so an
   * abandoned half-selection never leaks into the model.
   */
  protected readonly _start = linkedSignal(() => this.date()?.[0]);
  protected readonly _end = linkedSignal(() => this.date()?.[1]);

  /** Writable so setDisabledState can override the input. */
  protected readonly _disabled = linkedSignal(this.disabled);

  protected readonly _computedClass = computed(() =>
    hlm(
      'inline-flex w-full text-inherit relative z-[2] border-none bg-transparent items-center gap-2 whitespace-nowrap rounded-md ring-offset-background transition-colors border border-input h-auto px-0 py-0 justify-start',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      'disabled:pointer-events-none disabled:opacity-50',
      '[&_ng-icon]:pointer-events-none [&_ng-icon]:shrink-0',
      !this._start() && !this._end() ? 'text-muted-foreground' : '',
      this.userClass(),
    ),
  );

  /**
   * Reflects the in-flight selection, not the committed model, so the trigger
   * updates as soon as a start date is picked.
   */
  protected readonly formattedDate = computed(() => {
    const start = this._start();
    const end = this._end();
    return start || end ? this.formatDates()([start, end]) : undefined;
  });

  protected _onChange?: ChangeFn<[T, T] | null>;
  protected _onTouched?: TouchFn;

  protected _handleStartDateChange(value: T | undefined): void {
    this._start.set(value);
  }

  protected _handleEndDateChange(value: T | undefined): void {
    this._end.set(value);
    if (this._disabled()) return;

    const start = this._start();
    if (!start || !value) return;

    const transformed = this.transformDates()([start, value]);

    this.date.set(transformed);
    this._onChange?.(transformed);
    this.changed.emit(transformed);

    if (this.autoCloseOnEndSelection() && this.popover?.close) {
      this.popover.close(true, 100);
    }
  }

  /**
   * Discard a half-finished selection when the popover closes, reverting to
   * whatever was last committed.
   */
  protected _onClose(): void {
    this._onTouched?.();

    const committed = this.date();
    if (this._start() && !this._end()) {
      this._start.set(committed?.[0]);
      this._end.set(committed?.[1]);
    }
  }

  public open(): void {
    this.popover?.open?.();
  }

  public close(): void {
    this.popover?.close?.(true, 100);
  }

  public reset(): void {
    this.date.set(undefined);
    this._start.set(undefined);
    this._end.set(undefined);
    this._onChange?.(null);
    this.changed.emit(null);
  }

  /** CONTROL VALUE ACCESSOR */
  public writeValue(value: [T, T] | null): void {
    untracked(() => {
      this.date.set(value ? this.transformDates()(value) : undefined);
    });
  }

  public registerOnChange(fn: ChangeFn<[T, T] | null>): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: TouchFn): void {
    this._onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this._disabled.set(isDisabled);
  }
}
